import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Clock,
  Compass,
  CreditCard,
  Crown,
  Handshake,
  LifeBuoy,
  Loader2,
  Lock,
  Minus,
  ShieldCheck,
  Sparkles,
  Telescope,
  Unlock,
  type LucideIcon,
} from "lucide-react";
import { NIVA_AVATAR_POSTER } from "@/components/NivaAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { TierSelector, PENDING_STRIPE_PAYMENT_KEY, readPendingStripePayment } from "@/components/TierSelector";
import { useToast } from "@/components/ui/toast";
import { api } from "@/lib/api";
import type { User } from "@/types";
import { orgScopedPath } from "@/lib/orgDoor";
import { PLAN_COMPARE_GROUPS, PLAN_FEATURES, type PlanCellValue } from "@/lib/tierFeatures";

// ── Static marketing content ──────────────────────────────────────────────
// Prices mirror the live tiers (Explorer $79/mo · $588/yr, Operator $497/mo ·
// $3,588/yr). The annual figure shown as "/mo" is the effective monthly rate.
// Feature content (card bullets + comparison rows) lives in @/lib/tierFeatures
// so the upgrade and benefits dialogs on the subscription page describe a plan
// exactly the way this page does. Only pricing/marketing chrome is local.
type CellValue = PlanCellValue;

type Plan = {
  id: number;
  name: string;      // real Stripe tier name — used to resolve the live tier id
  display: string;   // label shown on the card
  monthly: number;
  annual: number;
  annualMonthly: number;
  savePct: number;
  tagline: string;
  highlight: boolean;
  badge?: string;
  cta: string;
  features: string[];
};

// NOTE: `monthly`/`annual` here are DISPLAY figures. The actual charge comes from
// the live Stripe tier resolved by `name`. If these prices/names change, update the
// backend tiers to match (see `/lookups/tiers`) — checkout charges the real price.
const PLANS: Plan[] = [
  {
    id: 1,
    name: "Explorer",
    display: "Explorer",
    monthly: 79,
    annual: 588,
    annualMonthly: 49,
    savePct: 38,
    tagline: "For active searchers",
    highlight: false,
    cta: "Choose Explorer",
    features: PLAN_FEATURES[0],
  },
  {
    id: 2,
    name: "Operator",
    display: "Operator",
    monthly: 497,
    annual: 3588,
    annualMonthly: 299,
    savePct: 40,
    tagline: "For serious acquirers",
    highlight: true,
    badge: "Most popular",
    cta: "Choose Operator",
    features: PLAN_FEATURES[1],
  },
];

// The free default plan — every buyer without an active subscription is on this.
// Not a Stripe tier, so it has no checkout: its CTA is a status label.
const BASIC_FEATURES = [
  "Browse all Active and Coming Soon listings",
  "2 free deal unlocks every week, carried over all month — full detail + BL Advisor chat",
  "View listing category",
  "Deal financials — asking price, revenue, EBITDA & multiples",
  "Basic filters",
];

// Fully hands-off tier — no Stripe checkout, CTA routes to support/BL experts.
const CUSTOM_FEATURES = [
  "Dedicated acquisition expert end-to-end",
  "We source & vet deals to your buy-box",
  "Valuation, LOI & negotiation handled for you",
  "Due diligence & financing coordination",
  "White-glove closing support",
  "Everything in Operator included",
];

const STEPS = [
  { icon: CreditCard, title: "Add card + BuyBox", body: "Enter payment and complete your buyer profile to activate your membership." },
  { icon: Unlock, title: "Full access", body: "Browse, unlock, and analyze every off-market deal the moment you subscribe." },
  { icon: Sparkles, title: "Source & unlock", body: "Run AI analysis, generate LOIs, and lock the deals you want to pursue." },
  { icon: ShieldCheck, title: "Continue or cancel", body: "Keep going, or cancel anytime before your renewal — no penalty, no questions." },
];

const COMPARE = PLAN_COMPARE_GROUPS;

const FAQS = [
  { q: "How does membership billing work?", a: "You add a card and complete your BuyBox buyer profile, then get full access to everything. You're charged for your first billing period today, and it renews on that same date each cycle — cancel anytime before renewal." },
  { q: "Can I cancel anytime?", a: "Yes. Cancel from your account settings at any time. Cancel before your renewal date and you won't be charged again." },
  { q: "Are memberships refundable?", a: "Cancel before renewal to avoid any charge. Once a billing period starts, it runs to the end of that period." },
  { q: "What is the success fee and when do I pay it?", a: "A success fee is only paid when you actually close a deal — never up front. Your membership covers sourcing, analysis, and locks." },
  { q: "What is a BuyBox profile?", a: "Your BuyBox captures the deals you're looking for — size, industry, geography and more — so matching and alerts surface the right listings." },
  { q: "Is my payment secure?", a: "Payments are processed by Stripe with 256-bit encryption. We never store your card details on our servers." },
  { q: "What do the 2 free unlocks on Basic include?", a: "Every account starts on Basic and can open 2 deals a week in full — the complete narrative and the exact location — with no card on file. On a deal you've unlocked you can also message its BL Advisor, make an offer and sign the NDA. You earn 2 more each week, and unused unlocks carry over until the month ends (up to 8 a month); the count starts fresh on the 1st. Favorites, deal locks and the SBA calculator are member features and are not part of the free unlocks." },
  { q: "What's the real difference between Explorer and Operator?", a: "Explorer is built for sourcing with 18 unlocks a month and core AI matching. Operator adds unlimited unlocks, deal locks, 24-hour early access, Niva (the BL AI Deal Assistant) beyond your free first week on Explorer, and the full AI analysis suite." },
];

// ── Small presentational helpers ──────────────────────────────────────────
function CompareCell({ value, accent }: { value: CellValue; accent?: boolean }) {
  if (value === true) {
    return (
      <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${accent ? "bg-primary/20 text-primary" : "bg-emerald-500/15 text-emerald-400"}`}>
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (value === false) {
    return <Minus className="mx-auto h-4 w-4 text-muted-foreground/50" />;
  }
  return <span className={`text-sm font-semibold ${accent ? "text-primary" : "text-foreground/80"}`}>{value}</span>;
}

function FeatureList({ items }: { items: string[] }) {
  return (
    <ul className="mt-6 space-y-3 border-t border-border/70 pt-6">
      {items.map((f) => (
        <li key={f} className="flex items-start gap-2.5 text-[13.5px] leading-5 text-foreground/80">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
            <Check className="h-3 w-3" strokeWidth={3} />
          </span>
          <span>{f}</span>
        </li>
      ))}
    </ul>
  );
}

// Plan header: icon tile + mono eyebrow name + tagline.
function PlanHeader({ icon: Icon, name, tagline, accent }: { icon: LucideIcon; name: string; tagline: string; accent?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${accent ? "bg-gold-gradient text-primary-foreground shadow-md shadow-primary/25" : "bg-primary/10 text-primary"}`}>
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <p className={`font-mono text-[0.72rem] font-semibold uppercase tracking-[0.2em] ${accent ? "text-primary" : "text-foreground/70"}`}>{name}</p>
        <p className="mt-0.5 text-[13px] text-muted-foreground">{tagline}</p>
      </div>
    </div>
  );
}

// Little "Niva is in this plan" strip shown on the paid cards.
function NivaChip({ label }: { label: string }) {
  return (
    <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-primary/25 bg-primary/[0.06] px-3 py-2">
      <img src={NIVA_AVATAR_POSTER} alt="" aria-hidden className="h-7 w-7 shrink-0 rounded-full object-cover ring-2 ring-primary/40" />
      <span className="text-[12.5px] font-semibold leading-4 text-foreground/85">{label}</span>
    </div>
  );
}

const PLAN_ICONS: Record<string, LucideIcon> = { Explorer: Telescope, Operator: Crown };

const NIVA_CHIP_LABEL: Record<string, string> = {
  Explorer: "Try Niva, your AI deal assistant, free for 1 week",
  Operator: "Niva, your AI deal assistant — unlimited",
};

const NIVA_POINTS = [
  "Answers questions about any listing, instantly",
  "Explains financials, multiples and BuyBox fit",
  "Helps you compare deals and plan next steps",
];

export default function ChoosePlanPage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState<"month" | "year">("month");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  // Which tier's payment flow is open (null = closed). Picking a plan jumps
  // straight to payment for that tier via TierSelector's direct mode.
  const [payTierId, setPayTierId] = useState<number | null>(null);
  // "Contact BL experts" (Custom plan): emails the BL team + an ack to the buyer.
  const [customLoading, setCustomLoading] = useState(false);
  const [customSent, setCustomSent] = useState(false);
  // The marketing PLANS below are keyed by NAME. Real tier ids are SERIAL and
  // differ per environment (see the 2026_06_19 migration) — never hardcode them.
  // Resolve name → live id from the API so the payment modal gets a valid tier.
  const [tierIdByName, setTierIdByName] = useState<Record<string, number>>({});

  const isAnnual = billing === "year";

  // Open at the top. Upgrade CTAs live far down the pages that link here, and
  // react-router keeps the outgoing scroll position on navigation — so without
  // this the plans page opens halfway down (often at the FAQ) and reads as
  // broken. "auto", not "smooth": this is a page entry, not a scroll gesture.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get<{ id: number; name: string }[]>("/lookups/tiers")
      .then((tiers) => {
        if (cancelled) return;
        setTierIdByName(Object.fromEntries(tiers.map((t) => [t.name, t.id])));
      })
      .catch(() => { /* clicks no-op until tiers resolve; toast handled downstream */ });
    return () => { cancelled = true; };
  }, []);

  const handleContactExperts = async () => {
    if (customSent || customLoading) return;
    setCustomLoading(true);
    try {
      await api.post("/payments/custom-plan-interest", {});
      setCustomSent(true);
      toast.success("Request received! A BL expert will reach out shortly — a confirmation email is on its way to you.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send your request. Please try again.");
    } finally {
      setCustomLoading(false);
    }
  };

  const openSelector = (planName: string) => {
    const realId = tierIdByName[planName];
    if (realId != null) setPayTierId(realId);
  };

  const handleTierSelected = async (selection: { tierId: number; subscriptionId?: string; promoCode?: string }) => {
    if (!user) return;
    setLoading(true);
    try {
      await api.post("/payments/activate-subscription", {
        tier_id: selection.tierId,
        stripe_subscription_id: selection.subscriptionId ?? null,
        promo_code: selection.promoCode ?? null,
      });
      // Subscription is attached to the account — the redirect-resume stash is done.
      sessionStorage.removeItem(PENDING_STRIPE_PAYMENT_KEY);
      // Refresh user so has_active_subscription becomes true
      const updated = await api.get<User>("/auth/me");
      updateUser(updated);
      toast.success("Subscription activated! Welcome to BusinessLocating.");
      navigate(orgScopedPath("/buyer/home", user?.org_slug));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to activate subscription. Please contact support.");
    } finally {
      setLoading(false);
    }
  };

  // ── Resume activation after a redirect-based Stripe payment ──────────────
  // stripe.confirmPayment can leave this page entirely (bank redirects etc.);
  // TierSelector stashed the paid subscription in sessionStorage first, and
  // Stripe returns the browser here with ?redirect_status=…. Attach the
  // ALREADY-PAID subscription to the account — reloading retries safely, and an
  // activation that never completes is cancelled + refunded by the daily sweep.
  const [searchParams] = useSearchParams();
  const resumeAttemptedRef = useRef(false);
  useEffect(() => {
    if (!user || resumeAttemptedRef.current) return;
    const redirectStatus = searchParams.get("redirect_status");
    if (!redirectStatus) return;
    const pendingPayment = readPendingStripePayment();
    if (!pendingPayment) return;
    resumeAttemptedRef.current = true;

    if (redirectStatus !== "succeeded" && redirectStatus !== "processing") {
      sessionStorage.removeItem(PENDING_STRIPE_PAYMENT_KEY);
      toast.error("Payment was not completed. Please try again.");
      return;
    }
    void handleTierSelected({ tierId: pendingPayment.tierId, subscriptionId: pendingPayment.subscriptionId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const isBasicCurrent = !user?.has_active_subscription;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── Hero ───────────────────────────────────────────────────────
          public-hero-shell keeps the Dark · Gold look on either theme;
          hero-niva is the flat ground the Niva hero art was shot against. */}
      <section className="px-4 pt-6 sm:px-6 lg:pt-8">
        <div className="public-hero-shell hero-niva relative mx-auto max-w-6xl rounded-3xl px-6 py-10 text-foreground sm:px-10 lg:py-14">
          <img
            src="/images/home-banners/niva-hero.webp"
            alt=""
            aria-hidden
            className="pointer-events-none absolute bottom-0 right-0 hidden h-full w-[46%] max-w-[32rem] object-cover object-[50%_10%] [mask-image:linear-gradient(to_right,transparent,black_30%)] md:block"
          />

          <div className="relative z-10 max-w-xl md:max-w-[56%]">
            <span className="public-pill inline-flex items-center gap-2 rounded-full border px-3.5 py-1 font-mono text-[0.68rem] font-semibold uppercase tracking-[0.22em]">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Membership
            </span>
            <h1 className="heading-bold mt-5 font-display text-[2.1rem] leading-[1.08] text-foreground sm:text-5xl lg:text-[3.4rem]">
              Pick the tier that matches <span className="text-primary">your pace.</span>
            </h1>
            <p className="mt-4 max-w-lg text-[15px] leading-7 text-foreground/70">
              From casual exploration to a fully hands-off, expert-run acquisition. 24-hour trial on all paid tiers.
            </p>

            <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-[13px] font-medium text-foreground/75">
              <li className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-primary" /> 24-hour trial</li>
              <li className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Secured by Stripe</li>
              <li className="flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" /> Cancel before renewal</li>
            </ul>

            {/* Billing toggle */}
            <div className="mt-7 inline-flex items-center rounded-2xl border border-border bg-card/70 p-1 backdrop-blur">
              <button
                type="button"
                onClick={() => setBilling("month")}
                aria-pressed={!isAnnual}
                className={`rounded-xl px-5 py-2 text-sm font-bold transition-all ${!isAnnual ? "bg-gold-gradient text-primary-foreground shadow-md shadow-primary/25" : "text-foreground/70 hover:text-foreground"}`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBilling("year")}
                aria-pressed={isAnnual}
                className={`flex items-center gap-2 rounded-xl px-5 py-2 text-sm font-bold transition-all ${isAnnual ? "bg-gold-gradient text-primary-foreground shadow-md shadow-primary/25" : "text-foreground/70 hover:text-foreground"}`}
              >
                Annual
                <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${isAnnual ? "bg-black/15 text-primary-foreground" : "bg-emerald-500/15 text-emerald-400"}`}>
                  Save 40%
                </span>
              </button>
            </div>
          </div>

          {/* Niva speech bubble — over the hero art on md+, inline on phones */}
          <div className="relative z-10 mt-8 flex max-w-sm items-center gap-3 rounded-2xl border border-primary/30 bg-card/85 p-3 pr-4 shadow-xl backdrop-blur md:absolute md:bottom-8 md:right-8 md:mt-0 md:max-w-[17rem]">
            <img src={NIVA_AVATAR_POSTER} alt="Niva" className="h-11 w-11 shrink-0 rounded-full object-cover ring-2 ring-primary/50" />
            <p className="text-[12.5px] leading-[1.35rem] text-foreground/85">
              <span className="font-semibold text-primary">Niva:</span> Not sure which plan fits? Most serious buyers start on <span className="font-semibold text-foreground">Operator</span>.
            </p>
          </div>
        </div>
      </section>

      {/* ── Pricing cards ──────────────────────────────────────────── */}
      <section className="px-4 pt-12 pb-10 sm:px-6">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-stretch gap-5 md:grid-cols-2 xl:grid-cols-4">
          {/* Basic (free) — the default plan when a buyer has no subscription */}
          <div className="relative flex flex-col rounded-3xl border border-border bg-card p-6 transition-shadow hover:shadow-lg">
            <PlanHeader icon={Compass} name="Basic" tagline="For browsing the surface" />
            <div className="mt-6 flex items-end gap-1.5">
              <span className="font-display text-[2.75rem] font-bold leading-none tracking-tight tabular-nums text-foreground">$0</span>
              <span className="pb-1.5 text-sm font-medium text-muted-foreground">/mo</span>
            </div>
            <p className="mt-2 h-5 text-[13px] text-muted-foreground">Free forever</p>

            <button
              type="button"
              disabled
              className={`mt-5 flex w-full cursor-default items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold ${
                isBasicCurrent
                  ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-500"
                  : "border border-border bg-muted text-muted-foreground"
              }`}
            >
              {isBasicCurrent && <Check className="h-4 w-4" />}
              {isBasicCurrent ? "Current plan" : "Free plan"}
            </button>

            <FeatureList items={BASIC_FEATURES} />
          </div>

          {PLANS.map((plan) => {
            const price = isAnnual ? plan.annualMonthly : plan.monthly;
            const isSelected = payTierId != null && payTierId === tierIdByName[plan.name];
            const Icon = PLAN_ICONS[plan.name] ?? Sparkles;
            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-3xl border p-6 transition-all ${
                  isSelected
                    ? "border-primary bg-primary/[0.06] ring-2 ring-primary/60"
                    : plan.highlight
                    ? "border-primary/70 bg-gradient-to-b from-primary/[0.12] via-card to-card shadow-[0_24px_60px_-20px_rgba(212,169,62,0.55)] xl:-translate-y-3"
                    : "border-border bg-card hover:shadow-lg"
                }`}
              >
                {plan.highlight && plan.badge && (
                  <span className="bg-gold-gradient absolute -top-3.5 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 font-mono text-[0.64rem] font-bold uppercase tracking-[0.18em] text-primary-foreground shadow-lg shadow-primary/30">
                    <Sparkles className="h-3 w-3" />
                    {plan.badge}
                  </span>
                )}

                <PlanHeader icon={Icon} name={plan.display} tagline={plan.tagline} accent={plan.highlight} />
                <div className="mt-6 flex items-end gap-1.5">
                  <span className="font-display text-[2.75rem] font-bold leading-none tracking-tight tabular-nums text-foreground">${price.toLocaleString()}</span>
                  <span className="pb-1.5 text-sm font-medium text-muted-foreground">/mo</span>
                  {isAnnual && (
                    <span className="mb-1.5 ml-1 text-sm font-semibold tabular-nums text-muted-foreground/70 line-through">${plan.monthly}</span>
                  )}
                </div>
                {isAnnual ? (
                  <p className="mt-2 h-5 text-[13px] text-muted-foreground">
                    <span className="font-semibold text-foreground/75">${plan.annual.toLocaleString()}</span> billed yearly ·{" "}
                    <span className="font-semibold text-emerald-500">save {plan.savePct}%</span>
                  </p>
                ) : (
                  <p className="mt-2 h-5 text-[13px] text-muted-foreground">billed monthly</p>
                )}

                <button
                  type="button"
                  onClick={() => openSelector(plan.name)}
                  className={`group mt-5 flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all ${
                    isSelected || plan.highlight
                      ? "bg-gold-gradient text-primary-foreground shadow-lg shadow-primary/25 hover:brightness-105"
                      : "border border-primary/50 bg-transparent text-primary hover:bg-primary/10"
                  }`}
                >
                  {isSelected ? (
                    <><Check className="h-4 w-4" /> Selected</>
                  ) : (
                    <>{plan.cta}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>
                  )}
                </button>

                {NIVA_CHIP_LABEL[plan.name] && <NivaChip label={NIVA_CHIP_LABEL[plan.name]} />}

                <FeatureList items={plan.features} />
              </div>
            );
          })}

          {/* Custom — fully hands-off, expert-run. No checkout: talk to BL experts. */}
          <div className="relative flex flex-col rounded-3xl border border-border bg-card p-6 transition-shadow hover:shadow-lg">
            <PlanHeader icon={Handshake} name="Custom" tagline="First-time buyers · fully hands-off" />
            <div className="mt-6 flex items-end">
              <span className="font-display text-[2.4rem] font-bold leading-none tracking-tight text-foreground">Let’s talk</span>
            </div>
            <p className="mt-2 h-5 text-[13px] text-muted-foreground">Sourcing → closing, by BL experts</p>

            <button
              type="button"
              onClick={handleContactExperts}
              disabled={customLoading || customSent}
              className={`mt-5 flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all ${
                customSent
                  ? "cursor-default border border-emerald-500/40 bg-emerald-500/10 text-emerald-500"
                  : "border border-primary/50 bg-transparent text-primary hover:bg-primary/10 disabled:opacity-60"
              }`}
            >
              {customLoading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
              ) : customSent ? (
                <><Check className="h-4 w-4" /> Request sent</>
              ) : (
                "Contact BL experts"
              )}
            </button>

            <FeatureList items={CUSTOM_FEATURES} />
          </div>
        </div>

        <p className="mx-auto mt-8 flex max-w-3xl items-start justify-center gap-2 text-center text-xs leading-5 text-muted-foreground">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          <span>All memberships non-refundable · Success fee is separate and due at closing only · 24-hour trial requires card + completed BuyBox profile.</span>
        </p>
      </section>

      {/* ── Meet Niva ──────────────────────────────────────────────── */}
      <section className="px-4 py-6 sm:px-6">
        <div className="mx-auto grid max-w-5xl grid-cols-1 items-center gap-8 overflow-hidden rounded-3xl border border-border bg-card p-7 sm:p-9 md:grid-cols-[auto_1fr]">
          <div className="relative mx-auto w-44 sm:w-52">
            <div className="absolute -inset-3 rounded-[2rem] bg-primary/20 blur-2xl" />
            <img
              src={NIVA_AVATAR_POSTER}
              alt="Niva, the BusinessLocating AI deal assistant"
              className="relative aspect-square w-full rounded-[1.75rem] object-cover ring-2 ring-primary/40"
            />
            <span className="absolute -bottom-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border border-primary/40 bg-card px-3 py-1 text-[11px] font-bold text-foreground shadow-md">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
              Online 24/7
            </span>
          </div>
          <div>
            <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-primary">Meet Niva</p>
            <h2 className="heading-bold mt-2 font-display text-2xl leading-tight text-foreground sm:text-[2rem]">
              Your AI deal assistant, built into every listing.
            </h2>
            <ul className="mt-4 space-y-2">
              {NIVA_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm leading-6 text-foreground/80">
                  <Sparkles className="mt-1 h-4 w-4 shrink-0 text-primary" />
                  {p}
                </li>
              ))}
            </ul>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => openSelector("Operator")}
                className="bg-gold-gradient inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:brightness-105"
              >
                Get unlimited Niva with Operator
                <ArrowRight className="h-4 w-4" />
              </button>
              <span className="text-[13px] text-muted-foreground">Explorer includes a free first week.</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Cost-of-acquisition band ───────────────────────────────── */}
      <section className="px-4 py-6 sm:px-6">
        <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl border border-border bg-card p-7 sm:p-9">
          <div className="grid grid-cols-1 items-center gap-6 md:grid-cols-[1.4fr_1fr]">
            <div>
              <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-primary">The real cost of acquisition</p>
              <p className="mt-3 font-display text-xl font-semibold leading-snug text-foreground sm:text-2xl">
                A broker can take <span className="text-primary">$100,000+</span> in commission. Operator is
                <span className="text-foreground"> $3,588 / year flat</span> — success fee paid{" "}
                <span className="text-foreground">only when you close</span>.
              </p>
            </div>
            <div className="flex items-center justify-around gap-4 rounded-2xl border border-border bg-muted p-5">
              <div className="text-center">
                <p className="font-mono text-[0.66rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Broker</p>
                <p className="mt-1 font-display text-3xl font-bold tabular-nums text-muted-foreground line-through">$100k+</p>
              </div>
              <div className="h-12 w-px bg-border" />
              <div className="text-center">
                <p className="font-mono text-[0.66rem] font-semibold uppercase tracking-[0.16em] text-primary">BL flat</p>
                <p className="mt-1 font-display text-3xl font-bold tabular-nums text-primary">$3,588</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How membership works ───────────────────────────────────── */}
      <section className="px-4 py-14 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-primary">4 simple steps</p>
            <h2 className="heading-bold mt-2 font-display text-3xl text-foreground sm:text-4xl">How membership works</h2>
            <p className="mt-3 text-muted-foreground">Full access from the moment you subscribe.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={step.title} className="group relative rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg">
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary transition-colors group-hover:bg-gold-gradient group-hover:text-primary-foreground">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="font-display text-3xl font-bold tabular-nums text-foreground/10">0{i + 1}</span>
                  </div>
                  <h3 className="heading-bold mt-4 text-base text-foreground">{step.title}</h3>
                  <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{step.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Feature comparison ─────────────────────────────────────── */}
      <section className="px-4 py-4 sm:px-6">
        <div className="mx-auto max-w-4xl">
          <div className="text-center">
            <h2 className="heading-bold font-display text-3xl text-foreground sm:text-4xl">Compare every feature</h2>
            <p className="mt-3 text-muted-foreground">Exactly what you get — nothing buried in the fine print.</p>
          </div>

          {/* `overflow-hidden` alone squeezed the four plan columns into slivers
              on a phone; scroll the table sideways inside the card instead. */}
          <div className="mt-8 overflow-x-auto rounded-2xl border border-border bg-card">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-5 py-4 font-mono text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Feature</th>
                  <th className="px-4 py-4 text-center">
                    <span className="flex flex-col items-center gap-0.5">
                      <span className="text-sm font-bold text-foreground/85">Explorer</span>
                      <span className="text-xs font-medium tabular-nums text-muted-foreground">${(isAnnual ? PLANS[0].annualMonthly : PLANS[0].monthly).toLocaleString()}/mo</span>
                    </span>
                  </th>
                  <th className="bg-primary/[0.06] px-4 py-4 text-center">
                    <span className="flex flex-col items-center gap-0.5">
                      <span className="flex items-center gap-1 text-sm font-bold text-primary"><Crown className="h-3.5 w-3.5" /> Operator</span>
                      <span className="text-xs font-medium tabular-nums text-muted-foreground">${(isAnnual ? PLANS[1].annualMonthly : PLANS[1].monthly).toLocaleString()}/mo</span>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((section) => (
                  <Fragment key={section.group}>
                    <tr>
                      <td colSpan={2} className="px-5 pt-6 pb-2 font-mono text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-primary/90">
                        {section.group}
                      </td>
                      <td className="bg-primary/[0.06]" />
                    </tr>
                    {section.rows.map(([label, explorer, operator]) => (
                      <tr key={label} className="border-b border-border/60 transition-colors last:border-0 hover:bg-muted/30">
                        <td className="px-5 py-3.5 text-sm text-foreground/80">{label}</td>
                        <td className="px-4 py-3.5 text-center"><CompareCell value={explorer} /></td>
                        <td className="bg-primary/[0.06] px-4 py-3.5 text-center"><CompareCell value={operator} accent /></td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────────── */}
      <section className="px-4 py-14 sm:px-6">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <h2 className="heading-bold font-display text-3xl text-foreground sm:text-4xl">Questions, answered</h2>
            <p className="mt-3 text-muted-foreground">The real billing, cancellation, and refund terms — in plain terms.</p>
          </div>
          <div className="mt-8 space-y-3">
            {FAQS.map((faq, i) => {
              const open = openFaq === i;
              return (
                <div
                  key={faq.q}
                  className={`overflow-hidden rounded-2xl border bg-card transition-colors ${open ? "border-primary/40" : "border-border"}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? null : i)}
                    aria-expanded={open}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="text-sm font-semibold text-foreground sm:text-base">{faq.q}</span>
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-all ${open ? "rotate-180 bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                      <ChevronDown className="h-4 w-4" />
                    </span>
                  </button>
                  {open && <p className="px-5 pb-5 text-sm leading-7 text-muted-foreground">{faq.a}</p>}
                </div>
              );
            })}
          </div>

          {/* Still stuck on a question the FAQ doesn't answer → open a ticket.
              Buyers on this page are past legal onboarding, so the shell-wrapped
              /buyer/support route (sidebar + nav) is always reachable for them. */}
          <div className="mt-8 flex flex-col items-center justify-between gap-4 rounded-2xl border border-primary/25 bg-gradient-to-r from-primary/[0.08] to-transparent px-5 py-5 text-center sm:flex-row sm:text-left">
            <div className="flex items-center gap-3">
              <img src={NIVA_AVATAR_POSTER} alt="" aria-hidden className="h-11 w-11 shrink-0 rounded-full object-cover ring-2 ring-primary/40" />
              <div>
                <p className="text-sm font-semibold text-foreground">Still have questions before you pick a plan?</p>
                <p className="flex items-center justify-center gap-1.5 text-[13px] text-muted-foreground sm:justify-start">
                  <LifeBuoy className="h-3.5 w-3.5 text-primary" /> Open a ticket and the BL team will help you choose.
                </p>
              </div>
            </div>
            <Link
              to={orgScopedPath("/buyer/support", user?.org_slug)}
              className="bg-gold-gradient inline-flex shrink-0 items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-bold shadow-md shadow-primary/20 transition-all hover:brightness-105"
            >
              Contact support
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Direct payment (skips the comparison table) ─────────────── */}
      {payTierId !== null && (
        <TierSelector
          key={payTierId}
          registrationEmail={user?.email || ""}
          onTierSelected={handleTierSelected}
          isLoading={loading}
          directTierId={payTierId}
          directInterval={billing}
          onClose={() => setPayTierId(null)}
        />
      )}
    </div>
  );
}
